import {
  SelectiveSyncConfiguration,
  ServiceAccountFieldsResponse,
  ConditionsByName,
  ConditionSchemasByID,
  EndUserAccountType,
  NextPageResponse,
  OrganizationMetadataResponse,
  SftpCredentialFieldsResponse,
  ReportFields,
  LinkingFlowActionEnum,
  CommonModelScope,
  EditCommonModelScopeResponse,
  AgentHandlerLinkEndUserDetails,
} from "../config/types";
import { RegisteredUserFlowFormData } from "../flow/MergeLinkModalRoot";
import { fetchWithoutAuth, HttpError } from "./apiClient";

export const getNextPage = ({
  apiBaseURL,
  linkToken,
  forAdmin = false,
  currentPage,
  formData,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  forAdmin: boolean;
  currentPage: string;
  formData: RegisteredUserFlowFormData;
  onResponse: (response: NextPageResponse) => void;
  onError: (error: Error) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: "connectors/link/linking-flow/process-step",
    method: "POST",
    body: {
      link_token: linkToken,
      for_admin: forAdmin,
      current_page: currentPage,
      form_data: formData,
    },
    onResponse,
    onError,
  });
};

export const getEndUserConfigurabilityData = ({
  apiBaseURL,
  linkToken,
  linkedAccountID,
  integrationID,
  category,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  linkedAccountID: string;
  integrationID: string;
  category: string;
  onResponse: (response: CommonModelScope[]) => void;
  onError: (error: Error) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: "connectors/link/linking-flow/get-end-user-configurable-scopes",
    method: "POST",
    body: {
      link_token: linkToken,
      linked_account_id: linkedAccountID,
      integration_id: integrationID,
      category: category,
    },
    onResponse,
    onError,
  });
};

export const editEndUserConfigurabilityData = ({
  apiBaseURL,
  linkToken,
  linkedAccountID,
  integrationID,
  category,
  modelName,
  action,
  fieldName,
  isEnabled,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  linkedAccountID: string;
  integrationID: string;
  category: string;
  modelName: string;
  action?: string;
  fieldName?: string;
  isEnabled: boolean;
  onResponse: (response: EditCommonModelScopeResponse) => void;
  onError: (error: Error) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: "integrations/linking-flow/edit-end-user-configurable-scopes",
    method: "POST",
    body: {
      link_token: linkToken,
      linked_account_id: linkedAccountID,
      integration_id: integrationID,
      category: category,
      model_name: modelName,
      action: action,
      field_name: fieldName,
      is_enabled: isEnabled,
    },
    onResponse,
    onError,
  });
};

export const fetchOrganizationMetadata = ({
  apiBaseURL,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  onResponse: (response: OrganizationMetadataResponse) => void;
  onError: (error: Error) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `integrations/link/organization-metadata`,
    method: "POST",
    body: {
      link_token: linkToken,
    },
    onResponse,
    onError,
  });
};

export const checkOAuthCompletion = ({
  apiBaseURL,
  OAuthKey,
  onResponse,
}: {
  apiBaseURL: string;
  OAuthKey: string;
  onResponse: (message: { is_complete: boolean }) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/connectors/registered-users/oauth/check/${OAuthKey}`,
    method: "GET",
    onResponse,
  });
};

export const initiateOAuth = ({
  apiBaseURL,
  connectorID,
  registeredUserID,
  linkToken,
  authOptionName,
  registeredUserSecrets,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  connectorID: string;
  registeredUserID: string;
  linkToken: string;
  authOptionName: string;
  registeredUserSecrets: any;
  onResponse: (response: { oauth_key: string; oauth_authorize_url: string }) => void;
  onError: (errorMessage?: string) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/connectors/${connectorID}/registered-users/${registeredUserID}/oauth/init`,
    method: "POST",
    body: {
      link_token: linkToken,
      auth_option_name: authOptionName,
      registered_user_secrets: registeredUserSecrets,
    },
    onResponse,
    onError: (error, errorBody) => {
      const status = error instanceof HttpError ? error.status : undefined;
      const message =
        status === 400 &&
        errorBody &&
        typeof errorBody === "object" &&
        typeof errorBody.error === "string"
          ? errorBody.error
          : undefined;
      onError(message);
    },
  });
};

export const initiateTrelloOAuth = ({
  apiBaseURL,
  linkedAccountID,
  integrationID,
  onResponse,
  customDomain,
  onError,
}: {
  apiBaseURL: string;
  linkedAccountID: string;
  integrationID: string;
  customDomain: string | undefined;
  onResponse: (response: { oauth_key: string; oauth_authorize_url: string }) => void;
  onError: () => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/linked-accounts/oauth/1/init/${linkedAccountID}/${integrationID}`,
    body: {
      custom_domain: customDomain,
    },
    method: "POST",
    onResponse,
    onError,
  });
};

const OAUTH_POPUP_WIDTH = 600;
const OAUTH_POPUP_HEIGHT = 800;

// Explicit dimensions are what make browsers open a popup instead of a new tab;
// a tab covers the customer's page entirely. The popup is clamped to the screen
// and centered on the customer's browser window so placement doesn't vary by browser.
const getOAuthPopupFeatures = () => {
  const width = Math.min(OAUTH_POPUP_WIDTH, window.screen.availWidth);
  const height = Math.min(OAUTH_POPUP_HEIGHT, window.screen.availHeight);
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  return `width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)},menubar=no,toolbar=no`;
};

export const openOAuthAuthorizeURL = (
  oauth_authorize_url: string,
  subdomain: undefined | string,
  target: string = "oauth_window"
) => {
  const constructedUrl = oauth_authorize_url.replace("{DOMAIN}", subdomain ?? "");
  return window.open(constructedUrl, target, getOAuthPopupFeatures());
};

export const logLinkActivity = ({
  apiBaseURL,
  linkToken,
  eventName,
  onSuccess,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  eventName: string;
  onSuccess?: (response: any) => void;
  onError?: (response: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/log-link-activity`,
    method: "POST",
    body: { event_name: eventName, link_token: linkToken },
    onResponse: () => {
      if (onSuccess !== undefined) {
        onSuccess({});
      }
    },
    onError: onError,
  });
};

export const logLinkExecutionRecord = ({
  apiBaseURL,
  linkToken,
  eventName,
  onSuccess,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  eventName: LinkingFlowActionEnum;
  onSuccess: (response: any) => void;
  onError?: (response: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `connectors/link/linking-flow-execution-event`,
    method: "POST",
    body: { action: eventName, link_token: linkToken },
    onResponse: onSuccess,
    onError: onError,
  });
};

export const checkLinkedAccount = ({
  apiBaseURL,
  linkedAccountId,
  integrationId,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkedAccountId: string;
  integrationId: string;
  onResponse: (data: any) => void;
  onError: (data: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/linked-accounts/${linkedAccountId}/check-linking-fields`,
    method: "POST",
    body: { integrationId: integrationId },
    onResponse: onResponse,
    onError: onError,
  });
};

export const fetchServiceAccountFields = ({
  apiBaseURL,
  stepId,
  selectedCategory,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  stepId: string;
  selectedCategory: string;
  linkToken: string;
  onResponse: (data: ServiceAccountFieldsResponse) => void;
  onError: (data: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/${selectedCategory}/${stepId}/service-account`,
    method: "POST",
    body: { link_token: linkToken },
    onResponse,
    onError,
  });
};

export const fetchSftpCredentialFields = ({
  apiBaseURL,
  stepId,
  selectedCategory,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  stepId: string;
  selectedCategory: string;
  linkToken: string;
  onResponse: (data: SftpCredentialFieldsResponse) => void;
  onError: (data: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/${selectedCategory}/${stepId}/sftp-credentials`,
    method: "POST",
    body: { link_token: linkToken },
    onResponse,
    onError,
  });
};

export const updateSftpCredentialFields = ({
  apiBaseURL,
  stepId,
  selectedCategory,
  linkToken,
  publicKey,
  pgpPublicKey,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  stepId: string;
  selectedCategory: string;
  linkToken: string;
  publicKey?: string;
  pgpPublicKey?: string;
  onResponse: () => void;
  onError: (data: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/${selectedCategory}/${stepId}/sftp-credentials`,
    method: "PATCH",
    body: { link_token: linkToken, public_key: publicKey, pgp_public_key: pgpPublicKey },
    onResponse,
    onError,
  });
};

export const fetchSftpRequiredFields = ({
  apiBaseURL,
  stepId,
  selectedCategory,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  stepId: string;
  selectedCategory: string;
  linkToken: string;
  onResponse: (data: ReportFields) => void;
  onError: (data: any) => void;
}): void => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/${selectedCategory}/${stepId}/sftp-required-fields`,
    method: "POST",
    body: { link_token: linkToken },
    onResponse,
    onError,
  });
};

export const mapConditionsByName = (configurationList: SelectiveSyncConfiguration[]) => {
  const newConditions: ConditionsByName = {};
  for (const condition of configurationList?.[0]?.linked_account_conditions || []) {
    newConditions[condition.common_model] = newConditions[condition.common_model] || [];
    newConditions[condition.common_model].push(condition);
  }
  return newConditions;
};

export const setLinkChoiceStepOptions = ({
  apiBaseURL,
  linkToken,
  configurationName,
  integrationName,
  endUserAccountType,
  category,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  configurationName: string;
  integrationName: string;
  endUserAccountType: EndUserAccountType | undefined;
  category: string;
  onResponse: (data: string[]) => void;
  onError: (data: any) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/link/${integrationName}/set-link-choice-step-options?category=${category}`,
    method: "POST",
    body: {
      configuration_name: configurationName,
      end_user_account_type: endUserAccountType,
      link_token: linkToken,
    },
    onResponse,
    onError,
  });
};

export const endLinkingFlowAndKickoffInitialSync = ({
  apiBaseURL,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  linkedAccountID?: string;
  shouldNotEndLinkingFlow?: boolean;
  onResponse: (data: ConditionSchemasByID) => void;
  onError: (data: any) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: `/integrations/linking-flow/end-linking-flow-and-kickoff-initial-sync`,
    method: "POST",
    body: {
      link_token: linkToken,
    },
    onResponse,
    onError,
  });
};


/**
 * Resolves the end user and the customer organization behind a link token.
 *
 * Only called when telemetry is on, so these identifiers are never requested for
 * a browser that has analytics switched off.
 */
export const getEndUserDetails = ({
  apiBaseURL,
  linkToken,
  onResponse,
  onError,
}: {
  apiBaseURL: string;
  linkToken: string;
  onResponse: (data: AgentHandlerLinkEndUserDetails) => void;
  onError?: (error: Error) => void;
}) => {
  fetchWithoutAuth({
    apiBaseURL,
    path: "connectors/link/linking-flow/end-user-details",
    method: "POST",
    body: {
      link_token: linkToken,
    },
    onResponse,
    onError,
  });
};
